import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TablePagination,
  CircularProgress,
  Button,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  PurchaseOrdersProvider,
  usePurchaseOrders,
} from "../../contexts/albina/PurchaseOrdersContext";
import * as XLSX from "xlsx";
import STORE_NAMES, { getStoreFromUrl } from "../../utils/STORE_NAMES";
/**
 * PurchasesByGood page
 *
 * - Select a Good -> shows all POs containing that good
 * - Each table row = one PO that contains that good (aggregated if multiple lines)
 * - Columns: PO Ref, Date, Supplier, Unit price (PO), Quantity, Unit, Subtotal, Tax, Total w/ tax
 * - Footer totals: sum qty / subtotal / tax / total
 *
 * Usage:
 *  - Put this file in your pages folder and import it into routes
 *  - It's wrapped in PurchaseOrdersProvider by default (remove wrapper if you already have it)
 */

// ---------------- styles (kept consistent with your other page)
const floatIn = keyframes`
  0% { transform: translateY(8px); opacity: 0; }
  60% { transform: translateY(-6px); opacity: 1; }
  100% { transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Root = styled("div")(({ theme }) => ({
  minHeight: "100%",
  padding: theme.spacing(3),
  background:
    "radial-gradient(circle at 10% 10%, rgba(6,182,212,0.06), transparent 10%), radial-gradient(circle at 90% 90%, rgba(99,102,241,0.04), transparent 10%), linear-gradient(180deg, rgba(246,248,255,0.6), rgba(250,250,255,0.94))",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  fontSize: "1.6rem",
  letterSpacing: "-0.2px",
  background: "linear-gradient(90deg, #06b6d4, #6366f1)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundSize: "200% 100%",
  animation: `${shimmer} 3s linear infinite`,
  fontWeight: 700,
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.8), rgba(250,250,255,0.9))",
  boxShadow: "0 8px 30px rgba(2,6,23,0.04)",
  padding: theme.spacing(2),
}));

// ---------------- helper - compute aggregated line for a good inside a PO
function buildGoodLineFromPO(
  po,
  goodId,
  goodsList,
  suppliersList,
  categoriesList
) {
  if (!po || !Array.isArray(po.po_inv_items)) return null;
  const entries = po.po_inv_items.filter(
    (it) => String(it.good) === String(goodId)
  );
  if (!entries.length) return null;

  // aggregate quantity, subtotal, tax
  const qty = entries.reduce((s, e) => s + (Number(e.quantity) || 0), 0);
  const sumPriceQty = entries.reduce(
    (s, e) => s + Number(e.price_used || 0) * (Number(e.quantity) || 0),
    0
  );
  const subtotal = sumPriceQty;
  // If each entry has total_tax_used use it, otherwise try tax_used * qty
  const taxFromEntries = entries.reduce(
    (s, e) =>
      s +
      (Number(e.total_tax_used) ||
        Number(e.tax_used || 0) * (Number(e.quantity) || 0)),
    0
  );

  // fallback to good supplier tax rate if nothing present
  const goodObj = goodsList?.find((g) => String(g._id) === String(goodId));
  const goodSupplier = goodObj?.goods_suppliers?.[0] || {};
  const taxRate = Number(goodSupplier?.tax_rate || 0);

  const tax = taxFromEntries > 0 ? taxFromEntries : subtotal * (taxRate / 100);
  const unitPrice = qty
    ? sumPriceQty / qty
    : entries[0]?.price_used || Number(goodSupplier?.price) || 0;
  const totalWithTax = subtotal + tax;

  const supplierId = entries[0]?.supplier || null;
  const supplier = suppliersList?.find(
    (s) => String(s._id) === String(supplierId)
  );

  const categoryId = goodObj?.category;
  const category = categoriesList?.find(
    (c) => String(c._id) === String(categoryId)
  );
  const categoryName = category?.name || "N/A";

  return {
    poRef:
      po.order_no ||
      po.invoice_no ||
      (po._id ? `#${String(po._id).slice(0, 8)}` : "N/A"),
    poId: po._id,
    date: po.received_date || po.orderDate || null,
    supplierId,
    supplierName: supplier?.name || supplierId || "N/A",
    unitPrice,
    qty,
    measurement_unit:
      goodObj?.measurement_unit || goodSupplier?.measurement_unit || "N/A",
    subtotal,
    tax,
    totalWithTax,
    categoryName,
    goodId,
    goodName: goodObj?.name || "N/A",
    rawPo: po,
  };
}

const endpoint0 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_0;
const endpoint1 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_1;
const endpoint2 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_2;

const baseUrl = process.env.REACT_APP_BASE_URL;
// ---------------- the main component
const PurchasesByGood = () => {
  const {
    purchaseOrders = [],
    purchaseOrders1 = [],
    purchaseOrders2 = [],
    purchaseOrders3 = [],
    purchaseOrders4 = [],
    purchaseOrders5 = [],
    purchaseOrders6 = [],
    purchaseOrders7 = [],
    goods = [],
    suppliers = [],
    categories = [],
    loading,
    error,
  } = usePurchaseOrders();
  // storeIds from endpoints

  // Store names are now resolved directly from po.storeId

  // UI state
  const [selectedGood, setSelectedGood] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedStore, setSelectedStore] = useState("");

  // combine all stores' POs into single array
  const allPOs = useMemo(
    () => [
      ...(purchaseOrders || []),
      ...(purchaseOrders1 || []),
      ...(purchaseOrders2 || []),
      ...(purchaseOrders3 || []),
      ...(purchaseOrders4 || []),
      ...(purchaseOrders5 || []),
      ...(purchaseOrders6 || []),
      ...(purchaseOrders7 || []),
    ],
    [
      purchaseOrders,
      purchaseOrders1,
      purchaseOrders2,
      purchaseOrders3,
      purchaseOrders4,
      purchaseOrders5,
      purchaseOrders6,
      purchaseOrders7
    ]
  );

  const getStoreNameForPO = (po) => {
    const names = [
      "BOON COFFEE ROASTERS CAFE-FALAK",
      "BOON COFFEE ROASTERS CAFE LLC - JUMEIRAH PARK",
      "BOON COFFEE ROASTERS CAFE - PADEL CLUB",
      "BOON COFFEE ROASTERS FZCO - GOLDEN MILE",
      "BOON COFFEE ROASTERS CAFÉ LLC - MEADOWS VILLAGE",
      "BOON COFFEE ROASTERS CAFÉ LLC (BRANCH) - FORTE TOWER",
      "BOON PRODUCTION KITCHEN",
      "Dubai Hills"
    ];
    return names[po.storeIndex] || "Unknown Store";
  };

  // collect unique good IDs from all POs or from selected store's POs
  const uniqueGoodIds = useMemo(() => {
    const ids = new Set();
    const posToCheck = selectedStore
      ? allPOs.filter((po) => getStoreNameForPO(po) === selectedStore)
      : allPOs;
    posToCheck.forEach((po) => {
      if (po.po_inv_items) {
        po.po_inv_items.forEach((item) => {
          if (item.good) ids.add(String(item.good));
        });
      }
    });
    return ids;
  }, [allPOs, selectedStore]);

  const availableGoods = useMemo(() => {
    let filtered = goods.filter((g) => uniqueGoodIds.has(String(g._id)));
    if (selectedSupplier) {
      filtered = filtered.filter((g) =>
        g.goods_suppliers?.some(
          (gs) => String(gs.supplier) === String(selectedSupplier)
        )
      );
    }
    if (selectedCategory) {
      filtered = filtered.filter(
        (g) => String(g.category) === String(selectedCategory)
      );
    }
    return filtered;
  }, [goods, uniqueGoodIds, selectedSupplier, selectedCategory, selectedStore]);

  const availableCategories = useMemo(() => {
    if (!selectedSupplier) return categories;
    const supplierGoods = goods.filter((g) =>
      g.goods_suppliers?.some(
        (gs) => String(gs.supplier) === String(selectedSupplier)
      )
    );
    const catIds = new Set(supplierGoods.map((g) => g.category));
    return categories.filter((c) => catIds.has(c._id));
  }, [categories, goods, selectedSupplier]);

  const availableStores = useMemo(() => {
    const storeNames = new Set();
    allPOs.forEach((po) => {
      storeNames.add(getStoreNameForPO(po));
    });
    return Array.from(storeNames).sort();
  }, [allPOs]);

  const [supplierFilter, setSupplierFilter] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // compute rows (POs that contain the selected good). If no good selected, rows = []
  const rows = useMemo(() => {
    if (
      !selectedGood &&
      !selectedCategory &&
      !selectedSupplier &&
      !selectedStore
    )
      return [];

    let allLines = [];

    if (selectedGood) {
      const matchedPOs = allPOs.filter((po) =>
        po.po_inv_items.some(
          (item) => String(item.good) === String(selectedGood)
        )
      );
      matchedPOs.forEach((po) => {
        const line = buildGoodLineFromPO(
          po,
          selectedGood,
          goods,
          suppliers,
          categories
        );
        if (line) {
          line.storeName = getStoreNameForPO(po);
          allLines.push(line);
        }
      });
    } else if (selectedCategory) {
      const matchedPOs = allPOs.filter((po) =>
        po.po_inv_items.some((item) => {
          const good = goods.find((g) => String(g._id) === String(item.good));
          return good && String(good.category) === String(selectedCategory);
        })
      );
      matchedPOs.forEach((po) => {
        const goodIdsInPO = po.po_inv_items
          .map((item) => String(item.good))
          .filter((gid, index, arr) => arr.indexOf(gid) === index); // unique

        goodIdsInPO.forEach((goodId) => {
          const goodObj = goods.find((g) => String(g._id) === goodId);
          if (
            goodObj &&
            String(goodObj.category) === String(selectedCategory)
          ) {
            const line = buildGoodLineFromPO(
              po,
              goodId,
              goods,
              suppliers,
              categories
            );
            if (line) {
              line.storeName = getStoreNameForPO(po);
              allLines.push(line);
            }
          }
        });
      });
    } else if (selectedSupplier) {
      const matchedPOs = allPOs.filter((po) =>
        po.po_inv_items.some(
          (item) => String(item.supplier) === String(selectedSupplier)
        )
      );
      matchedPOs.forEach((po) => {
        const goodIdsInPO = po.po_inv_items
          .filter((item) => String(item.supplier) === String(selectedSupplier))
          .map((item) => String(item.good))
          .filter((gid, index, arr) => arr.indexOf(gid) === index); // unique
        goodIdsInPO.forEach((goodId) => {
          const line = buildGoodLineFromPO(
            po,
            goodId,
            goods,
            suppliers,
            categories
          );
          if (line) {
            line.storeName = getStoreNameForPO(po);
            allLines.push(line);
          }
        });
      });
    } else if (selectedStore) {
      const matchedPOs = allPOs.filter(
        (po) => getStoreNameForPO(po) === selectedStore
      );
      matchedPOs.forEach((po) => {
        const goodIdsInPO = po.po_inv_items
          .map((item) => String(item.good))
          .filter((gid, index, arr) => arr.indexOf(gid) === index); // unique
        goodIdsInPO.forEach((goodId) => {
          const line = buildGoodLineFromPO(
            po,
            goodId,
            goods,
            suppliers,
            categories
          );
          if (line) {
            line.storeName = getStoreNameForPO(po);
            allLines.push(line);
          }
        });
      });
    }

    // apply filters (same as before)
    const bySupplier = supplierFilter
      ? allLines.filter((r) => String(r.supplierId) === String(supplierFilter))
      : allLines;

    const byStore = storeFilter
      ? bySupplier.filter((r) => r.storeName === storeFilter)
      : bySupplier;

    const fromDate = filterFrom ? new Date(filterFrom) : null;
    const toDate = filterTo ? new Date(filterTo) : null;
    const byDate = byStore.filter((r) => {
      if (!r.date) return true;
      const d = new Date(r.date);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    });

    const q = (search || "").trim().toLowerCase();
    const bySearch = q
      ? byDate.filter(
          (r) =>
            (r.poRef || "").toString().toLowerCase().includes(q) ||
            (r.supplierName || "").toString().toLowerCase().includes(q) ||
            (r.storeName || "").toString().toLowerCase().includes(q)
        )
      : byDate;

    bySearch.sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });

    return bySearch;
  }, [
    selectedGood,
    selectedCategory,
    selectedSupplier,
    selectedStore,
    allPOs,
    goods,
    suppliers,
    categories,
    supplierFilter,
    storeFilter,
    filterFrom,
    filterTo,
    search,
  ]);

  // footer totals
  const footerTotals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.qty += Number(r.qty || 0);
        acc.subtotal += Number(r.subtotal || 0);
        acc.tax += Number(r.tax || 0);
        acc.total += Number(r.totalWithTax || 0);
        return acc;
      },
      { qty: 0, subtotal: 0, tax: 0, total: 0 }
    );
  }, [rows]);

  // pagination slice
  const pageRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // export to excel
  const exportToExcel = () => {
    if (!selectedStore) return;

    // Prepare initial rows (numeric, not strings)
    let sheetRows = rows.map((r) => ({
      "PO Ref": r.poRef,
      Date: r.date,
      Store: r.storeName,
      Supplier: r.supplierName,
      Good: r.goodName,
      Category: r.categoryName,
      "Unit Price": Number(r.unitPrice || 0), // no .toFixed()
      Quantity: Number(r.qty || 0),
      Unit: r.measurement_unit,
      Subtotal: Number(r.subtotal || 0),
      Tax: Number(r.tax || 0),
      Total: Number(r.totalWithTax || 0),
    }));

    // Consolidate by Good
    const aggregatedMap = {};
    sheetRows.forEach((r) => {
      const good = r["Good"];
      if (!aggregatedMap[good]) {
        aggregatedMap[good] = { ...r, "PO Ref": "Consolidated" };
      } else {
        aggregatedMap[good]["Quantity"] += r["Quantity"];
        aggregatedMap[good]["Subtotal"] += r["Subtotal"];
        aggregatedMap[good]["Tax"] += r["Tax"];
        aggregatedMap[good]["Total"] += r["Total"];
      }
    });
    sheetRows = Object.values(aggregatedMap);

    // Add totals row
    const totalsRow = {
      "PO Ref": "Totals",
      Date: "",
      Store: "",
      Supplier: "",
      Good: "",
      Category: "",
      "Unit Price": "",
      Quantity: Number(footerTotals.qty || 0),
      Unit: "",
      Subtotal: Number(footerTotals.subtotal || 0),
      Tax: Number(footerTotals.tax || 0),
      Total: Number(footerTotals.total || 0),
    };
    sheetRows.push(totalsRow);

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(sheetRows);

    // === Apply Number Formatting ===
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const numberColumns = [
      "Unit Price",
      "Quantity",
      "Subtotal",
      "Tax",
      "Total",
    ];

    // Loop through rows/columns and format numeric cells
    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      // skip header row
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        const headerRef = XLSX.utils.encode_cell({ r: 0, c: C });
        const header = ws[headerRef]?.v;

        if (numberColumns.includes(header)) {
          const cell = ws[cellRef];
          if (cell && typeof cell.v === "number") {
            cell.t = "n"; // ensure numeric type
            cell.z = "#,##0.00"; // number format
          }
        }
      }
    }

    // === Bold totals row ===
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell_address = { c: C, r: range.e.r };
      const cell_ref = XLSX.utils.encode_cell(cell_address);
      if (!ws[cell_ref]) ws[cell_ref] = { t: "s", v: "" };
      ws[cell_ref].s = { font: { bold: true } };
    }

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Good Purchases");

    // File naming
    const name = selectedGood
      ? goods.find((g) => String(g._id) === String(selectedGood))?.name ||
        "Good"
      : selectedCategory
      ? categories.find((c) => String(c._id) === String(selectedCategory))
          ?.name || "Category"
      : suppliers.find((s) => String(s._id) === String(selectedSupplier))
          ?.name || "Supplier";

    XLSX.writeFile(
      wb,
      `Purchases_${name}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };


  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="260px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography color="error">Error: {String(error)}</Typography>
      </Box>
    );
  }

  return (
    <Root>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <div>
          <Title>Purchases by Good</Title>
          <Typography variant="body2" color="text.secondary">
            Select a store, supplier, category, or good to list every PO where
            that good has been purchased (aggregated per PO).
          </Typography>
        </div>

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={exportToExcel}
            disabled={!selectedStore || rows.length === 0}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      <GlassPaper>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <Autocomplete
            options={availableStores}
            getOptionLabel={(option) => option}
            value={selectedStore}
            onChange={(event, newValue) => {
              setSelectedStore(newValue || "");
              setSelectedSupplier(""); // reset supplier selection
              setSelectedCategory(""); // reset category selection
              setSelectedGood(""); // reset good selection
              setPage(0);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Store"
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            size="small"
          />

          <Autocomplete
            options={suppliers}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={suppliers.find((s) => s._id === selectedSupplier) || null}
            onChange={(event, newValue) => {
              setSelectedSupplier(newValue ? newValue._id : "");
              setSelectedCategory(""); // reset category selection
              setSelectedGood(""); // reset good selection
              setPage(0);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supplier"
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            size="small"
          />

          <Autocomplete
            options={availableCategories}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={
              availableCategories.find((c) => c._id === selectedCategory) ||
              null
            }
            onChange={(event, newValue) => {
              setSelectedCategory(newValue ? newValue._id : "");
              setSelectedGood(""); // reset good selection
              setPage(0);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            size="small"
          />

          <Autocomplete
            options={availableGoods}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={availableGoods.find((g) => g._id === selectedGood) || null}
            onChange={(event, newValue) => {
              setSelectedGood(newValue ? newValue._id : "");
              setPage(0);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Good"
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            size="small"
          />

          {/* <TextField
            label="Store (filter)"
            select
            size="small"
            sx={{ minWidth: 260 }}
            value={storeFilter}
            onChange={(e) => { setStoreFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All stores</MenuItem>
            {Object.entries(STORE_NAMES).map(([id, name]) => (
              <MenuItem key={id} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField> */}

          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filterFrom}
            onChange={(e) => {
              setFilterFrom(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filterTo}
            onChange={(e) => {
              setFilterTo(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          />

          <TextField
            label="Search (PO ref / supplier / store)"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 260 }}
          />
        </Box>

        <TableContainer
          component={Paper}
          sx={{ animation: `${floatIn} 400ms ease both` }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PO Ref</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Good</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Category</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pageRows.map((r) => (
                <TableRow key={`${r.poId}-${r.goodId}`}>
                  <TableCell>
                    <strong>{r.poRef}</strong>
                    {r.rawPo?.order_no ? (
                      <div style={{ fontSize: 11, color: "#666" }}>
                        {r.rawPo.order_no}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>{r.date || "N/A"}</TableCell>
                  <TableCell>{getStoreNameForPO(r.rawPo)}</TableCell>
                  <TableCell>{r.supplierName}</TableCell>
                  <TableCell>{r.goodName}</TableCell>
                  <TableCell align="right">
                    {Number(r.unitPrice || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="right">{Number(r.qty || 0)}</TableCell>
                  <TableCell>{r.measurement_unit}</TableCell>
                  <TableCell align="right">
                    {Number(r.subtotal || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {Number(r.tax || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {Number(r.totalWithTax || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>{r.categoryName}</TableCell>
                </TableRow>
              ))}

              {pageRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={12}
                    align="center"
                    style={{ padding: 24 }}
                  >
                    {!selectedGood &&
                    !selectedCategory &&
                    !selectedSupplier &&
                    !selectedStore
                      ? "Select a store, supplier, category, or good to start."
                      : "No purchase orders found with current filters."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={6}
                  style={{ fontWeight: 700, textAlign: "right" }}
                >
                  Totals (page filtered):
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {footerTotals.qty}
                </TableCell>
                <TableCell />
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {footerTotals.subtotal.toFixed(2)}
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {footerTotals.tax.toFixed(2)}
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {footerTotals.total.toFixed(2)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </Box>
      </GlassPaper>
    </Root>
  );
};

// Wrap with provider so this file can be used directly as a page.
// If your app already wraps routes with PurchaseOrdersProvider, remove the extra provider wrapper.
const PurchasesByGoodPage = () => (
  <PurchaseOrdersProvider
    endpoint0={
      `${baseUrl}/api/purchase-orders/purchase-orders-api-0`
    }
    endpoint1={
      `${baseUrl}/api/purchase-orders/purchase-orders-api-1`
    }
    endpoint2={
      `${baseUrl}/api/purchase-orders/purchase-orders-api-2`
    }
    endpoint3={
      `${baseUrl}/api/purchase-orders/purchase-orders-api-3`
    }
    endpoint4={
      `${baseUrl}/api/purchase-orders/purchase-orders-api-4`
    }
    endpoint5={
      `${baseUrl}/api/purchase-orders/purchase-orders-api-5`
    }
    endpoint6={
      `${baseUrl}/api/purchase-orders/purchase-orders-api-6`
    }
    endpoint7={
      `${baseUrl}/api/purchase-orders/purchase-orders-api-7`
    }
  >
    <PurchasesByGood />
  </PurchaseOrdersProvider>
);

export default PurchasesByGoodPage;
